import type { Dispatch, SetStateAction } from 'react'
import { useCallback } from 'react'
import type { Address } from 'src/components/checkout/steps/shipping/types'
import type { OrderForm } from 'src/sdk/checkout/entity/orderForm'
import { useGuestPostalFlow } from 'src/components/common/Header/PostalCode/Hooks/useGuestPostalFlow'
import { LocationOnIcon } from 'src/components/Icons/LocationOnIcon'
import { checkEnviromentIsB2B } from 'src/utils/checkEnviroment'
import { redirectToAccountOrLogin } from 'src/utils/redirectToAccountUrl'

import { GuestPostalLegalNotices } from './GuestPostalLegalNotices'

const CORREIOS_CEP_FINDER_HREF =
  'https://buscacepinter.correios.com.br/app/endereco/index.php'

interface PostalCodeModalContentForGuestProps {
  onModalClose: (args: {
    shouldTrackClose: boolean
    hasInputValue?: boolean
    buttonName?: string
  }) => void
  onConfirmGuestPostalCode: (value: string) => Promise<void>
  inputValue: string
  setInputValue: Dispatch<SetStateAction<string>>
  orderForm: OrderForm
  setOrderForm: (orderForm: OrderForm) => void
  setPostalCode: (postalCode: string) => void
  setProvisionalAddress: (address: Address | null) => void
  showModal: boolean
}

export function PostalCodeModalContentForGuest({
  onModalClose,
  onConfirmGuestPostalCode,
  inputValue,
  setInputValue,
  orderForm,
  setOrderForm,
  setPostalCode,
  setProvisionalAddress,
  showModal,
}: PostalCodeModalContentForGuestProps) {
  const continueFlowClose = useCallback(
    (buttonName: string) => {
      onModalClose({
        shouldTrackClose: true,
        buttonName,
      })
    },
    [onModalClose]
  )

  const {
    lookupError,
    isLookingUp,
    isConfirming,
    isApplyEnabled,
    addressPreview,
    handlePostalCodeChange,
    handleApplyClick,
    handleConfirmClick,
    handleUseOtherAddress,
  } = useGuestPostalFlow({
    showModal,
    inputValue,
    setInputValue,
    setProvisionalAddress,
    orderForm,
    setOrderForm,
    setPostalCode,
    onConfirmPostalCode: onConfirmGuestPostalCode,
    onContinueClose: continueFlowClose,
  })

  const hasAddressPreview = addressPreview.length > 0
  const loginHref = redirectToAccountOrLogin(checkEnviromentIsB2B())

  return (
    <div className="flex flex-col gap-6 font-medium items-center w-full">
      {!hasAddressPreview ? (
        <div className="flex flex-col gap-6 w-full items-start">
          <span className="text-base text-center w-full">
            Confira as{' '}
            <span className="text-postal-accent">melhores ofertas</span> e
            fretes para sua região
          </span>

          <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-row items-end gap-2 w-full">
              <div className="flex flex-col gap-2 min-w-0 flex-1">
                <label
                  htmlFor="guest-postal-code-input"
                  className="text-font-size-3 text-restructure-primary"
                >
                  CEP
                </label>
                <input
                  id="guest-postal-code-input"
                  name="postalCode"
                  data-testid="postal-code-input"
                  className="w-full h-12 rounded-full py-3 px-4 border-lightGray border text-base disabled:opacity-80 focus:border-primaryBlue"
                  placeholder="Insira seu CEP"
                  type="text"
                  value={inputValue}
                  onChange={handlePostalCodeChange}
                  disabled={isLookingUp || isConfirming}
                  maxLength={9}
                  aria-invalid={Boolean(lookupError)}
                  aria-describedby={
                    lookupError ? 'guest-postal-code-error' : undefined
                  }
                />
              </div>

              <button
                type="button"
                data-testid="postal-code-apply-button"
                className={`shrink-0 h-12 px-6 rounded-full bg-postal-accent text-restructure-primary-inverted text-sm font-medium ${
                  isApplyEnabled ? '' : 'opacity-40'
                }`}
                disabled={!isApplyEnabled || isConfirming}
                onClick={handleApplyClick}
              >
                {isLookingUp ? (
                  <div className="checkout-loader-button" />
                ) : (
                  'Aplicar'
                )}
              </button>
            </div>
            {lookupError ? (
              <p
                id="guest-postal-code-error"
                data-testid="postal-code-error"
                className="text-sm text-danger"
              >
                {lookupError}
              </p>
            ) : null}
          </div>

          <a
            href={CORREIOS_CEP_FINDER_HREF}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="postal-code-correios-link"
            className="text-sm text-postal-secondary underline w-fit font-normal"
          >
            Ir para o site dos correios
          </a>

          <p className="text-sm text-restructure-primary font-normal">
            Faça{' '}
            <a
              href={loginHref}
              data-testid="postal-code-login-link"
              className="font-bold underline"
            >
              login
            </a>{' '}
            para ver seus endereços salvos.
          </p>

          <GuestPostalLegalNotices />
        </div>
      ) : (
        <div className="flex flex-col gap-6 w-full items-center">
          <span className="text-base text-center w-full">
            Identificamos que você está no endereço abaixo, deseja utilizá-lo?
          </span>
          <div
            data-testid="postal-code-address-preview"
            title={addressPreview}
            className="flex w-full items-center gap-2 rounded-full border border-lightGray px-4 py-3 text-base"
          >
            <LocationOnIcon className="shrink-0" />
            <span className="truncate">{addressPreview}</span>
          </div>
          <button
            type="button"
            data-testid="postal-code-confirm-address-button"
            className="w-full h-12 bg-postal-accent text-body-md-link text-postal-inverse rounded-full disabled:opacity-40 transition-colors"
            disabled={isConfirming}
            onClick={handleConfirmClick}
          >
            {isConfirming ? (
              <div className="checkout-loader-button" />
            ) : (
              'Sim, utilizar endereço'
            )}
          </button>
          <button
            type="button"
            data-testid="postal-code-use-other-address-button"
            className="text-sm text-restructure-primary underline w-fit font-medium disabled:opacity-40"
            disabled={isConfirming}
            onClick={handleUseOtherAddress}
          >
            Não, utilizar outro endereço
          </button>
          <GuestPostalLegalNotices />
        </div>
      )}
    </div>
  )
}
